import { ART_PAL, fade, mix, n, rng, shade, tint, vgrad, type ArtTheme } from '../covers/kit';
import { defineScene, type SceneVariant } from './types';

/**
 * Daily words scene behind the field: a quiet study at dusk (a pale paper morning in light) — an ink-blue wash, blank
 * ivory letter tiles drifting in the side bands (no letters: the words are live text), a few found-word capsules in
 * teal and orange laid across tiles, an ink-brush dot trail and a red seal square at the lower right. The middle 40 %
 * of the wide scene stays calm (only the glow), every motif is marked `data-motif="<x>"`. No text, no filters.
 */
export function dailyWordsScene(theme: ArtTheme, v: SceneVariant): string {
  const P = ART_PAL[theme];
  const p = `sw${theme[0]}${v[0]}`;
  const wide = v === 'wide';
  const W = wide ? 1600 : 400;
  const H = wide ? 900 : 800;
  const o = (dark: number, light: number) => n(P.dark ? dark : light);

  const top = P.dark ? mix(P.bg, P.blue, 0.2) : mix(P.blue, '#FFFFFF', 0.9);
  const mid = P.dark ? mix(P.bg, P.purple, 0.1) : mix(P.purple, '#FFFFFF', 0.94);
  const bottom = P.dark ? mix(P.bg, P.blue, 0.06) : mix(P.gold, '#FFFFFF', 0.9);
  const ivory = P.dark ? '#E9DFC8' : '#FBF4E4';
  const tileEdge = P.dark ? shade(ivory, 0.5) : shade(ivory, 0.25);
  const inkCol = P.dark ? tint(P.blue, 0.2) : shade(P.blue, 0.25);

  const defs = `<defs>${vgrad(`${p}bg`, top, mid, bottom)}${fade(`${p}gl`, P.dark ? P.blue : P.purple, P.dark ? 0.14 : 0.12)}</defs>`;

  /** a blank tile, slightly turned */
  const tile = (x: number, y: number, s: number, rot: number) =>
    `<g transform="rotate(${rot} ${n(x)} ${n(y)})"><rect x="${n(x - s / 2)}" y="${n(y - s / 2)}" width="${n(s)}" height="${n(s)}" rx="${n(s * 0.2)}" fill="${ivory}" fill-opacity="${o(0.1, 0.7)}" stroke="${tileEdge}" stroke-opacity="${o(0.3, 0.35)}"/>` +
    `<rect x="${n(x - s * 0.2)}" y="${n(y - s * 0.05)}" width="${n(s * 0.4)}" height="${n(s * 0.1)}" rx="${n(s * 0.05)}" fill="${tileEdge}" fill-opacity="${o(0.45, 0.35)}"/></g>`;
  /** a cluster of tiles with a found-word capsule over a row of them */
  const cluster = (x: number, y: number, s: number, seed: number, col: string) => {
    const r = rng(seed);
    let g = '';
    for (let i = 0; i < 3; i++) g += tile(x + i * s * 1.15, y, s, Math.round((r() - 0.5) * 6));
    for (let i = 0; i < 2; i++)
      g += tile(x + (i + 0.5) * s * 1.15 + r() * 6, y + s * 1.25, s, Math.round((r() - 0.5) * 10));
    g += `<line x1="${n(x)}" y1="${n(y)}" x2="${n(x + 2 * s * 1.15)}" y2="${n(y)}" stroke="${col}" stroke-width="${n(s * 0.86)}" stroke-linecap="round" stroke-opacity="${o(0.28, 0.3)}"/>`;
    return `<g data-motif="${Math.round(x + s * 1.15)}">${g}</g>`;
  };
  /** an ink-brush trail of dots */
  const trail = (x: number, y: number, len: number, seed: number) => {
    const r = rng(seed);
    let d = '';
    for (let i = 0; i < 9; i++) {
      const cx = x + (i / 8) * len + (r() - 0.5) * 8;
      const cy = y + Math.sin(i / 1.6) * 14;
      d += `<circle cx="${n(cx)}" cy="${n(cy)}" r="${n(2 + r() * 4)}"/>`;
    }
    return `<g data-motif="${Math.round(x + len / 2)}" fill="${inkCol}" fill-opacity="${o(0.35, 0.3)}">${d}</g>`;
  };
  /** a small red seal square (an empty stamp: no characters) */
  const seal = (x: number, y: number, s: number) =>
    `<g data-motif="${Math.round(x)}"><rect x="${n(x - s / 2)}" y="${n(y - s / 2)}" width="${n(s)}" height="${n(s)}" rx="${n(s * 0.12)}" fill="${P.red}" fill-opacity="${o(0.55, 0.7)}"/>` +
    `<rect x="${n(x - s * 0.32)}" y="${n(y - s * 0.32)}" width="${n(s * 0.64)}" height="${n(s * 0.64)}" rx="${n(s * 0.06)}" fill="none" stroke="${ivory}" stroke-opacity="${o(0.5, 0.75)}" stroke-width="${n(s * 0.06)}"/></g>`;

  let art =
    `<rect width="${W}" height="${H}" fill="url(#${p}bg)"/>` +
    `<ellipse cx="${W / 2}" cy="${n(H * 0.45)}" rx="${n(W * 0.42)}" ry="${n(H * 0.44)}" fill="url(#${p}gl)"/>`;
  if (wide)
    art +=
      cluster(250, 230, 52, 3, P.teal) +
      cluster(1230, 170, 46, 7, P.orange) +
      cluster(1250, 600, 50, 11, P.teal) +
      tile(330, 640, 44, -8) +
      tile(1430, 420, 38, 9) +
      trail(200, 470, 230, 5) +
      seal(1400, 760, 46);
  // the phone: the column covers the whole width — only the wash and the glow (motifs would sit under the text)

  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" preserveAspectRatio="xMidYMid slice">${defs}${art}</svg>`;
}

/** Header accent of Daily words: a row of tiny blank tiles, every third one in teal (repeat-x tile, 18 × 8). */
export function dailyWordsAccent(theme: ArtTheme): string {
  const P = ART_PAL[theme];
  const ivory = P.dark ? '#E9DFC8' : shade('#FBF4E4', 0.12);
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 18 8" width="18" height="8">` +
    `<rect x="1" y="1" width="6" height="6" rx="1.5" fill="${ivory}" fill-opacity="${P.dark ? 0.55 : 0.9}"/>` +
    `<rect x="10" y="1" width="6" height="6" rx="1.5" fill="${P.teal}" fill-opacity="${P.dark ? 0.7 : 0.8}"/>` +
    `</svg>`
  );
}

export default defineScene({ game: 'daily-words', scene: dailyWordsScene, accent: dailyWordsAccent });
